import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, UserPlus, Crown, PaintBrush, UploadSimple, SpinnerGap, SealWarning, Copy, PaperPlaneTilt, Trash, DownloadSimple, Envelope, ShieldCheck, WarningCircle } from "@phosphor-icons/react";

const PLAN_FEATURES = {
  free: ["1 active election", "Up to 100 voters", "Core ballots & results"],
  starter: ["Unlimited elections", "Up to 1,000 voters", "CSV & PDF export, audit log"],
  pro: ["1,000+ voters", "Custom branding & watermark", "Priority support"],
};

const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Link copied"); };

function EmailHealth() {
  const [h, setH] = useState(null);
  const [sup, setSup] = useState([]);
  const [showDns, setShowDns] = useState(false);
  const loadedRef = useRef(false);
  const load = () => {
    api.get("/settings/email-health").then(({ data }) => setH(data)).catch(() => setH(false));
    api.get("/settings/suppressions").then(({ data }) => setSup(data || [])).catch(() => {});
  };
  useEffect(() => { if (loadedRef.current) return; loadedRef.current = true; load(); }, []);
  if (h === false) return null;

  const clear = async (email) => {
    try { await api.delete(`/settings/suppressions/${encodeURIComponent(email)}`); toast.success("Suppression cleared"); load(); }
    catch { toast.error("Could not clear"); }
  };

  const Row = ({ ok, label, value, warn }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-zinc-100 last:border-0">
      <span className="text-sm text-zinc-600">{label}</span>
      <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${ok ? "text-emerald-600" : warn ? "text-amber-600" : "text-zinc-800"}`}>
        {ok === true && <ShieldCheck size={15} weight="fill" />}
        {ok === false && <WarningCircle size={15} weight="fill" />}
        {value}
      </span>
    </div>
  );

  const rate = h?.delivery_rate_30d;
  return (
    <section className="mb-12" data-testid="email-health">
      <div className="flex items-center gap-2 mb-1">
        <Envelope size={22} weight="duotone" className="text-[#1F6FE5]" />
        <h2 className="font-display font-bold text-2xl tracking-tight">Email deliverability</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-5">How your voter invitations and alerts are reaching inboxes.</p>
      {!h ? (
        <div className="bg-white border border-zinc-200 p-6 text-sm text-zinc-400">Loading sender health…</div>
      ) : (
      <div className="bg-white border border-zinc-200 p-6">
        <Row ok={h.managed ? true : h.domain_verified === true ? true : h.domain_verified === false ? false : null}
             label="Sending domain"
             value={h.managed ? "Managed by SelectFlow" : (h.domain || "—") + (h.domain_verified === true ? " · verified" : h.domain_verified === false ? " · not verified" : "")} />
        <Row ok={h.webhook_configured} warn={!h.webhook_configured}
             label="Delivery event webhook"
             value={h.webhook_configured ? "Connected" : "Not connected"} />
        <Row label="Sender name" value={h.from_name} />
        {h.from_address && <Row label="From address" value={h.from_address} />}
        <Row ok={rate != null && rate >= 95 ? true : rate != null && rate < 90 ? false : null}
             label="Delivery rate (30 days)"
             value={rate != null ? `${rate}%` : "No data yet"} />
        <div className="flex gap-4 pt-3 text-xs text-zinc-500">
          <span data-testid="eh-delivered"><b className="text-emerald-600">{h.counts_30d?.delivered ?? 0}</b> delivered</span>
          <span data-testid="eh-bounced"><b className="text-amber-600">{h.counts_30d?.bounced ?? 0}</b> bounced</span>
          <span data-testid="eh-complained"><b className="text-red-500">{h.counts_30d?.complained ?? 0}</b> complaints</span>
          <span data-testid="eh-suppressed"><b className="text-zinc-700">{h.suppressed_count ?? 0}</b> suppressed</span>
        </div>

        {!h.managed && h.domain_verified !== true && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex items-start gap-2">
              <SealWarning size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold">Verify {h.domain || "your domain"} to protect deliverability</div>
                <div className="text-xs text-amber-800 mt-1">{h.domain_note || "Add the DNS records below (SPF/DKIM/DMARC) in your DNS provider, then re-check."}</div>
                {(h.dns_records?.length > 0) && (
                  <button data-testid="eh-toggle-dns" onClick={() => setShowDns((s) => !s)} className="mt-2 text-xs font-semibold underline">{showDns ? "Hide" : "Show"} DNS records</button>
                )}
              </div>
            </div>
            {showDns && (
              <div className="mt-3 space-y-2">
                {h.dns_records.map((r, i) => (
                  <div key={i} className="bg-white border border-amber-200 rounded p-2 text-[11px] font-mono break-all">
                    <span className="text-zinc-400">{r.type} {r.name ? `· ${r.name}` : ""}</span><br />{r.value}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!h.webhook_configured && (
          <p className="mt-4 text-xs text-zinc-400">To track bounces & complaints, add a Resend webhook pointing to <span className="font-mono text-zinc-500">{h.webhook_url}</span> and set <span className="font-mono">RESEND_WEBHOOK_SECRET</span>.</p>
        )}

        {sup.length > 0 && (
          <div className="mt-5" data-testid="suppression-list">
            <h4 className="text-sm font-semibold text-zinc-700 mb-2">Suppressed addresses</h4>
            <div className="divide-y divide-zinc-100 border border-zinc-200 rounded">
              {sup.map((s) => (
                <div key={s.email} data-testid={`sup-${s.email}`} className="flex items-center justify-between p-2.5 text-sm">
                  <div><span className="font-mono text-zinc-700">{s.email}</span> <span className="text-[10px] uppercase tracking-widest text-zinc-400 ml-2">{s.reason}</span></div>
                  <button data-testid={`sup-clear-${s.email}`} onClick={() => clear(s.email)} className="text-xs font-semibold text-[#1F6FE5] hover:underline">Clear</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
    </section>
  );
}

function EmailVerifyBanner() {
  const { emailVerified, refresh } = useAuth();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  if (emailVerified) return null;
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.status === "already_verified") { toast.success("Email already verified"); refresh(); return; }
      setLink(data.link);
      if (data.email_sent) toast.success("Verification email sent — check your inbox");
      else toast.message("Email couldn't be sent — use the link below to verify");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="verify-email-banner" className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <SealWarning size={22} weight="fill" className="text-amber-500 shrink-0" />
          <div>
            <div className="font-semibold text-sm text-amber-900">Verify your organisation email</div>
            <div className="text-xs text-amber-700">Confirm your email to unlock publishing. Didn't get it? Resend or use a direct link.</div>
          </div>
        </div>
        <button data-testid="resend-verification-btn" onClick={resend} disabled={busy} className="inline-flex items-center gap-2 px-3.5 py-2 bg-amber-600 text-white rounded-md text-sm font-semibold hover:bg-amber-700 transition-colors active:scale-95 disabled:opacity-50">
          {busy && <SpinnerGap size={15} className="animate-spin" />} Resend verification
        </button>
      </div>
      {link && (
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={link} data-testid="verify-link" className="flex-1 text-xs font-mono bg-white border border-amber-200 rounded px-2.5 py-1.5 text-amber-900" />
          <button data-testid="copy-verify-link" onClick={() => copyToClipboard(link)} className="px-3 py-1.5 border border-amber-300 rounded text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors">Copy link</button>
        </div>
      )}
    </div>
  );
}

function InviteDialog({ onDone }) {
  const { smsEnabled } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", phone: "", role: "manager" });
  const [result, setResult] = useState(null);
  const reset = () => { setF({ email: "", name: "", phone: "", role: "manager" }); setResult(null); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/team/invite", f);
      setResult(data);
      toast.success(data.email_sent ? "Invitation sent" : "Invite created — share the link below");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="invite-btn" className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95"><UserPlus size={16} /> Invite member</button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Invite team member</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="invite-result">
            <p className="text-sm text-zinc-600">
              Invitation for <strong>{f.email}</strong> created.
              {result.email_sent ? " We emailed them a link." : " Email delivery failed — share the link below directly."}
              {result.sms_sent && " An SMS was also sent."}
            </p>
            <div className="flex items-center gap-2">
              <input readOnly value={result.link} data-testid="invite-link" className="flex-1 text-xs font-mono bg-zinc-50 border border-zinc-200 rounded px-2.5 py-1.5 text-zinc-600" />
              <button data-testid="copy-invite-link" onClick={() => copyToClipboard(result.link)} className="px-3 py-1.5 border border-zinc-300 rounded text-xs font-semibold hover:border-black transition-colors">Copy</button>
            </div>
            <button onClick={reset} className="w-full h-10 border border-zinc-300 rounded-md text-sm font-semibold hover:border-black transition-colors">Invite another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label><Input data-testid="invite-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Phone (optional{smsEnabled ? "" : " — SMS not configured"})</Label>
              <Input data-testid="invite-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" placeholder="+233…" />
              <p className="text-[11px] text-zinc-400 mt-1">Adds an SMS fallback so the invite still lands if email is unreliable.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="invite-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="manager">Manager (elections only)</SelectItem>
                  <SelectItem value="admin">Admin (full control)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button data-testid="send-invite" className="w-full h-11 bg-[#1F6FE5] text-white rounded-md font-semibold hover:bg-[#1552C9] transition-colors active:scale-[0.98]">Send invitation</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Branding({ org, onSaved }) {
  const [color, setColor] = useState(org.branding?.primary_color || "#1F6FE5");
  const [logo, setLogo] = useState(org.branding?.logo_url || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setLogo(data.url); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    try { await api.put("/branding", { logo_url: logo, primary_color: color }); toast.success("Branding saved"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6 max-w-lg">
      <h3 className="font-display font-bold text-xl tracking-tight mb-1 flex items-center gap-2"><PaintBrush size={20} weight="duotone" /> Custom branding</h3>
      <p className="text-sm text-zinc-500 mb-5">Applied to your voting and public results pages.</p>
      <div className="flex items-center gap-4 mb-5">
        <div className="w-16 h-16 rounded-lg bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center">
          {logo ? <img src={fileUrl(logo)} alt="" className="w-full h-full object-contain" /> : <span className="text-xs text-zinc-400">Logo</span>}
        </div>
        <input ref={fileRef} data-testid="logo-input" type="file" accept="image/*" onChange={upload} className="hidden" />
        <button onClick={() => fileRef.current.click()} disabled={uploading} className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-md text-sm hover:border-black transition-colors">
          {uploading ? <SpinnerGap size={16} className="animate-spin" /> : <UploadSimple size={16} />} Upload logo
        </button>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <Label className="text-xs uppercase tracking-widest text-zinc-500">Brand colour</Label>
        <input data-testid="brand-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 rounded border border-zinc-200 cursor-pointer" />
        <span className="font-mono text-sm">{color}</span>
      </div>
      <button data-testid="save-branding" onClick={save} className="px-4 py-2.5 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95">Save branding</button>
    </div>
  );
}

export default function Settings() {
  const { user, org, refresh } = useAuth();
  const [billing, setBilling] = useState(null);
  const [team, setTeam] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const isAdmin = user?.role === "admin";

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.get("/settings/backup", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "selectflow-backup.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch { toast.error("Could not create backup"); }
    finally { setBackingUp(false); }
  };

  const load = (c) => {
    api.get("/billing", { params: c ? { country: c } : {} }).then(({ data }) => { setBilling(data); if (!country) setCountry(data.country); });
    api.get("/team").then(({ data }) => setTeam(data));
  };
  const [country, setCountry] = useState("");
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/billing/invoices").then(({ data }) => setInvoices(data || [])).catch(() => {}); }, []);

  const downloadInvoice = async (inv) => {    try {
      const res = await api.get(`/billing/invoices/${inv.reference}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download receipt"); }
  };
  const [params] = useSearchParams();
  const [checking, setChecking] = useState("");
  useEffect(() => {
    if (params.get("paid") === "1") { toast.success("Payment successful — plan active"); refresh(); }
  }, [params, refresh]);

  const RANK = { free: 0, starter: 1, pro: 2 };
  const changeCountry = (c) => { setCountry(c); load(c); };
  const local = (usd, id, cad) => {
    if (!billing || billing.display_currency === "USD") return null;
    const v = billing.converted?.[id]?.[cad];
    return v != null ? `${billing.display_symbol}${v} ${billing.display_currency}` : null;
  };

  const checkout = async (plan) => {
    setChecking(plan);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/billing/checkout", { plan, cadence: "monthly", callback_url, country });
      if (data.mock) { toast.success(`Switched to ${plan} plan`); load(country); refresh(); }
      else if (data.authorization_url) { window.location.href = data.authorization_url; }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setChecking(""); }
  };

  const downgrade = async () => {
    try { await api.post("/billing/upgrade", { plan: "free" }); toast.success("Switched to Free plan"); load(country); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resendInvite = async (id) => {
    try {
      const { data } = await api.post(`/team/invite/${id}/resend`);
      toast.success(data.email_sent ? "Invitation resent" : "Invite re-issued — share the copied link");
      load(country);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invitation? The link will stop working immediately.")) return;
    try { await api.delete(`/team/invite/${id}`); toast.success("Invitation revoked"); load(country); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const COUNTRIES = [["US","United States"],["GH","Ghana"],["NG","Nigeria"],["KE","Kenya"],["ZA","South Africa"],["GB","United Kingdom"],["CA","Canada"],["DE","Germany (EUR)"],["IN","India"],["EG","Egypt"]];
  const plans = ["free", "starter", "pro"];

  return (
    <Layout>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-1">Settings</h1>
      <p className="text-zinc-500 mb-8">Manage your plan, team and branding.</p>

      <EmailVerifyBanner />

      <section className="mb-12">
        <div className="flex flex-wrap items-baseline gap-3 mb-1">
          <h2 className="font-display font-bold text-2xl tracking-tight">Billing & plan</h2>
          {billing?.plan_expires_at && billing.plan !== "free" && (
            <span className="text-xs font-mono text-zinc-400">RENEWS/EXPIRES · {new Date(billing.plan_expires_at).toLocaleDateString()}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-sm text-zinc-500">Prices shown in your local currency. Charged in Ghana Cedis:</span>
          <Select value={country} onValueChange={changeCountry}>
            <SelectTrigger data-testid="country-select" className="w-52"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent className="bg-white">
              {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs font-medium text-zinc-600" data-testid="fx-note">You'll be charged in {billing?.charge_symbol || "GH₵"} (Ghana Cedis) via Paystack</span>
        </div>
        {!billing?.paystack_enabled && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-block mb-5 font-mono">⚠ PAYMENTS IN DEMO MODE — Paystack keys not set, upgrades apply instantly. Add keys to charge real cards.</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          {plans.map((id) => {
            const current = billing?.plan === id;
            const price = id === "free" ? "0" : billing?.pricing?.[id]?.monthly;
            const perEl = id !== "free" ? billing?.pricing?.[id]?.per_election : null;
            return (
              <div key={id} data-testid={`plan-${id}`} className={`bg-white border p-6 ${current ? "border-[#1F6FE5] ring-1 ring-[#1F6FE5]" : "border-zinc-200"}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5 font-display font-bold text-xl tracking-tight capitalize">{id === "pro" && <Crown size={18} weight="fill" className="text-[#1F6FE5]" />}{id}</div>
                  {current && <span data-testid={`plan-current-${id}`} className="text-[10px] uppercase tracking-widest font-bold text-[#1F6FE5]">Current</span>}
                </div>
                <div className="text-3xl font-display font-bold mb-1">${price}<span className="text-sm font-normal text-zinc-400">/mo</span></div>
                {id !== "free" && local(price, id, "monthly") && <div className="text-xs text-zinc-500" data-testid={`local-${id}`}>≈ {local(price, id, "monthly")}</div>}
                {perEl != null && <div className="text-xs text-zinc-500 mb-4 mt-1">or ${perEl} per election{local(perEl, id, "per_election") ? ` (≈ ${local(perEl, id, "per_election")})` : ""}</div>}
                {id === "free" && <div className="mb-4 h-4" />}
                <ul className="space-y-2 mb-6">
                  {PLAN_FEATURES[id].map((f) => <li key={f} className="flex items-start gap-2 text-sm text-zinc-600"><Check size={16} className="text-emerald-500 mt-0.5 shrink-0" weight="bold" /> {f}</li>)}
                </ul>
                {isAdmin && !current && id === "free" && (
                  <button data-testid="downgrade-free" onClick={downgrade} className="w-full py-2.5 border border-zinc-300 rounded-md font-semibold text-sm hover:border-black transition-colors">Switch to Free</button>
                )}
                {isAdmin && !current && id !== "free" && (
                  <button data-testid={`upgrade-${id}`} onClick={() => checkout(id)} disabled={checking === id} className="w-full py-2.5 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95 flex items-center justify-center gap-2">
                    {checking === id && <SpinnerGap size={16} className="animate-spin" />}
                    {RANK[id] > RANK[billing?.plan] ? "Upgrade" : "Switch"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-zinc-400 mt-3">Tip: need a bigger plan for just one vote? Open an election and choose <span className="font-medium">Buy this election</span> to unlock its limits without a monthly subscription.</p>

        {invoices.length > 0 && (
          <div className="mt-8" data-testid="billing-history">
            <h3 className="font-display font-bold text-lg tracking-tight mb-3">Billing history</h3>
            <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
              {invoices.map((inv) => (
                <div key={inv.reference} data-testid={`invoice-${inv.reference}`} className="flex items-center justify-between p-4">
                  <div>
                    <div className="font-medium text-sm">{inv.invoice_no} · {inv.kind === "nomination" ? "Nomination fee" : `${(inv.plan || "").replace(/^./, (m) => m.toUpperCase())} plan`}{inv.mock ? " (demo)" : ""}</div>
                    <div className="text-xs font-mono text-zinc-400">{(inv.issued_at || "").slice(0, 10)} · {inv.currency} {Number(inv.amount || 0).toFixed(2)}</div>
                  </div>
                  <button data-testid={`invoice-dl-${inv.reference}`} onClick={() => downloadInvoice(inv)} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1F6FE5] hover:underline">
                    <DownloadSimple size={15} /> Receipt
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {isAdmin && billing?.plan === "pro" && (
        <section className="mb-12"><Branding org={org} onSaved={refresh} /></section>
      )}

      {isAdmin && <EmailHealth />}

      {isAdmin && (
        <section className="mb-12" data-testid="data-backup">
          <div className="flex items-center gap-2 mb-1">
            <DownloadSimple size={22} weight="duotone" className="text-[#1F6FE5]" />
            <h2 className="font-display font-bold text-2xl tracking-tight">Data backup</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-5">Download a full, portable copy of your organisation's data.</p>
          <div className="bg-white border border-zinc-200 p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="max-w-xl">
              <div className="font-medium text-sm text-zinc-800">Export everything as a ZIP</div>
              <div className="text-xs text-zinc-500 mt-1">Elections, positions, candidates, voters, anonymous ballots, receipts, audit trail, invoices — plus a RESTORE guide. Ballots stay anonymous and secret credentials are excluded.</div>
            </div>
            <button data-testid="download-backup-btn" onClick={downloadBackup} disabled={backingUp}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95 disabled:opacity-50 whitespace-nowrap">
              {backingUp ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />} Download backup
            </button>
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-2xl tracking-tight">Team</h2>
          {isAdmin && <InviteDialog onDone={load} />}
        </div>
        <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
          {team?.members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-sm font-bold text-zinc-500">{m.name[0]?.toUpperCase()}</div>
                <div><div className="font-medium text-sm">{m.name}</div><div className="text-xs font-mono text-zinc-400">{m.email}</div></div>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 bg-zinc-100 px-2 py-1 rounded-sm">{m.role}</span>
            </div>
          ))}
          {team?.pending.map((p) => (
            <div key={p.id} data-testid={`pending-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-3 opacity-70">
                <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-sm font-bold text-zinc-400">?</div>
                <div><div className="font-medium text-sm">{p.email}</div><div className="text-xs font-mono text-zinc-400">{p.expired ? "Invitation expired" : "Invitation pending"}{p.phone ? ` · ${p.phone}` : ""}</div></div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && p.link && !p.expired && (
                  <button data-testid={`copy-pending-${p.id}`} onClick={() => copyToClipboard(p.link)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-300 rounded text-xs font-semibold text-zinc-600 hover:border-black transition-colors"><Copy size={13} /> Copy link</button>
                )}
                {isAdmin && (
                  <button data-testid={`resend-pending-${p.id}`} onClick={() => resendInvite(p.id)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-300 rounded text-xs font-semibold text-zinc-600 hover:border-black transition-colors"><PaperPlaneTilt size={13} /> Resend</button>
                )}
                {isAdmin && (
                  <button data-testid={`revoke-pending-${p.id}`} onClick={() => revokeInvite(p.id)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-300 rounded text-xs font-semibold text-zinc-600 hover:border-red-400 hover:text-red-500 transition-colors"><Trash size={13} /> Revoke</button>
                )}
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${p.expired ? "text-red-600 bg-red-50" : "text-amber-600 bg-amber-50"}`}>{p.role} · {p.expired ? "expired" : "pending"}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
      <div className="mt-16 -mx-5 sm:-mx-8"><Footer /></div>
    </Layout>
  );
}
